import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  BarChart, 
  Archive, 
  Folder 
} from 'lucide-react';
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

const HistoryCard = ({ icon: Icon, title, description, onClick }) => (
  <div 
    className="bg-white shadow-md rounded-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group"
    onClick={onClick}
  >
    <div className="flex items-center mb-4">
      <div className="bg-blue-100 text-blue-600 p-3 rounded-full mr-4">
        <Icon className="w-8 h-8" />
      </div>
      <h3 className="text-xl font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
        {title}
      </h3>
    </div>
    <p className="text-gray-600 mb-4">{description}</p>
    <div className="flex items-center text-gray-500 group-hover:text-blue-600 transition-colors">
      <span className="mr-2">View History</span>
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-5 w-5 transform group-hover:translate-x-1 transition-transform" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
      </svg>
    </div>
  </div>
);

const HistoryPage = () => {
  const navigate = useNavigate();

  const historyOptions = [
    {
      icon: FileText,
      title: 'Indent Approvals',
      description: 'View the history of indent approval processes, track statuses, and review past submissions.',
      route: '/history/indent_approval'
    },
    {
      icon: CheckCircle,
      title: 'Indent Items Tracking',
      description: 'Review completed projects, their timelines, and final status reports.',
      route: '/history/item_track'
    },
    {
      icon: Clock,
      title: 'Time Tracking',
      description: 'Explore historical time logs, billable hours, and project duration records.',
      route: '/time-tracking-history'
    },
    {
      icon: BarChart,
      title: 'Performance Metrics',
      description: 'Analyze historical performance data, trends, and key performance indicators.',
      route: '/performance-history'
    },
    {
      icon: Archive,
      title: 'Document Archives',
      description: 'Access archived documents, past reports, and historical records.',
      route: '/document-archives'
    },
    {
      icon: Folder,
      title: 'Project Folders',
      description: 'Browse through historical project folders and past project documentation.',
      route: '/project-folders'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">History Dashboard</h1>
          <p className="text-gray-600 max-w-2xl">
            Explore comprehensive historical records across various aspects of your projects and operations. 
            Access detailed insights, track past performances, and maintain a comprehensive view of your organizational history.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {historyOptions.map((option, index) => (
            <HistoryCard 
              key={index}
              icon={option.icon}
              title={option.title}
              description={option.description}
              onClick={() => navigate(option.route)}
            />
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HistoryPage;